/*  importModuleDefaults (context = webpack require.context)

    https://webpack.js.org/guides/dependency-management/#require-context

    the arguments passed to require.context must be literals - so a context
    must be defined and then submitted to this function that matches the
    condition for the desired imports.

    The second argument is a function that returns a schema transform for the
    key value (following the same pattern as mapObj(), described below)

    Example:

        context = require.context("@/components", true, /\.vue$/)

        modules = importModules(context, (key, item) => {
            return {
                // parse filename from system path, and pluck the default export
                [key.replace(/^.*(\\|\/|:)|\..*$/g, "")]: item.default
            };
        })
*/
export function importModules(context, schema) {
  return context.keys().reduce((defs, key) => Object.assign(defs, schema(key, context(key))), {});
}
/*
    And, for convenience, an implementation of the above. To be used when
    registering components in a .vue instance.
*/
export function importComponents(list) {
  const components = require.context('@/components', true, /\.vue$/);
  // search the components directory (and subdirectories) for .vue components
  return importModules(components, (key, item) => {
    // parse filename
    const name = key.replace(/^.*(\\|\/|:)|\..*$/g, '');
    // only include the modules requested in list
    return list && list.indexOf(name) === -1
      ? {}
      : {
        // return the default export
        [name]: item.default,
      };
  });
}
/*  mapObj (obj = object, schema = function)

    map object properties to schema values; i.e, to surface nested props or
    transform / insert other values through a schema function

    Example:

        pick = mapObj.bind(null, { key: { nested: value }})

        mapped = pick((key, item) => {
            return { [key]: item.nested }
        })
*/
export function mapObj(obj, schema) {
  return Object.keys(obj).reduce((defs, key) => Object.assign(defs, schema(key, obj[key])), {});
}

// self-explanatory
export function capitalize(str) {
  return str[0].toUpperCase() + str.slice(1);
}

// in case we need it...
export function toKebab(str) {
  return str
    .split(/(?=^[A-Z])|(?=.*)(?=[A-Z])/g)
    .join('-')
    .toLowerCase();
}

/*  typeCheck (obj = object, key = string, val)

    perform typechecking on a property key in obj for a given val

    Example:

        obj = {key: "value"}

        typeCheck(obj, "key", 1337) --> Error / val is Number; requires String
*/
export function typeCheck(obj, key, val) {
  // get the property prototype
  const type = Object.getPrototypeOf(obj[key]);
  // check against the value prototype
  if (type !== Object.getPrototypeOf(val)) {
    // print a nice error
    throw new TypeError(
      `typeCheck failed for prop "${key}" with value ${val}; requires ${type.constructor.name}`,
    );
  }
  return true;
}
/*
    helps with parsing errors from API Gateway.
*/
export function parseResponseJSON(res) {
  // does the response carry a status code of 4xx or 5xx?
  const err = !!String(res.status).match(/^[45]/);
  return res.json().then((data) => {
    if (err) {
      return Promise.reject(data.message || JSON.stringify(data));
    }
    return data;
  });
}
/*
    get a cheap unique id, when the value doesn't matter. From MDN:
    "It is guaranteed that a timeout ID will never be reused by a subsequent
    call to setTimeout() or setInterval()"
    https://developer.mozilla.org/en-US/docs/Web/API/WindowOrWorkerGlobalScope/setTimeout#Return_value
*/
export function uniqueId() {
  return setTimeout(() => null) || Math.round(new Date().getTime() / 1000);
}

// returns year from age for user
export function targetYear(user, attr) {
  [attr, 'currentAge'].forEach((prop) => {
    if (user[prop] === undefined) {
      throw new Error(`user.${prop} is undefined`);
    }
  });

  return new Date().getFullYear() + user[attr] - user.currentAge;
}

// returns age from year for user
export function targetAge(user, year) {
  if (typeof year !== 'number') {
    throw new Error('year must be a number');
  } else if (user.currentAge === undefined) {
    throw new Error('user.currentAge is undefined');
  }

  return year - new Date().getFullYear() + user.currentAge;
}

// returns info of position, width and height of a DOM element
export function getPositions(elem) {
  const element = elem.getBoundingClientRect();
  let elementPositions = {};

  if (element) {
    elementPositions = {
      top: element.top,
      bottom: element.bottom,
      left: element.left,
      right: element.right,
      width: element.width,
      height: element.height,
    };
  }
  return elementPositions;
}
